import React, { useCallback, useEffect, useState } from "react";
import { api, formatApiError } from "../lib/apiClient";
import PageHeader from "../components/PageHeader";
import { toast } from "sonner";
import { FieldStyle } from "./PartiesPage";
import { FloppyDisk, Warning, ArrowsClockwise, CalendarDots } from "@phosphor-icons/react";

export default function SettingsPage() {
  const [series, setSeries] = useState(null);
  const [saving, setSaving] = useState(false);
  const [fyList, setFyList] = useState([]);
  const [currentFy, setCurrentFy] = useState("");
  const [selectedFy, setSelectedFy] = useState("");
  const [counters, setCounters] = useState([]);
  const [savingCounter, setSavingCounter] = useState(null);

  const load = useCallback(async () => {
    const { data } = await api.get("/settings/document-series");
    setSeries(data);
    try {
      const fy = await api.get("/settings/fy-list");
      setFyList(fy.data.fy_list);
      setCurrentFy(fy.data.current_fy);
      setSelectedFy(fy.data.current_fy);
    } catch (err) { if (err?.response?.status !== 403) console.error('Settings load failed:', err); }
  }, []);
  useEffect(() => { load(); }, [load]);

  useEffect(() => {
    if (!selectedFy) return;
    api.get("/settings/counters", { params: { fy: selectedFy } })
      .then(r => setCounters(r.data.rows))
      .catch((err) => { console.error('Counters load failed:', err); setCounters([]); });
  }, [selectedFy]);

  if (!series) return <div className="p-8 text-ink-muted">Loading settings…</div>;

  const update = (dt, patch) => {
    setSeries(s => ({ ...s, [dt]: { ...s[dt], ...patch } }));
  };

  const preview = (cfg) => {
    const seq = String(cfg.next_number || 1).padStart(cfg.pad || 4, "0");
    let yearSeg = "";
    if (cfg.include_year) {
      if (cfg.use_fy) {
        const now = new Date();
        const startYear = now.getMonth() < 3 ? now.getFullYear() - 1 : now.getFullYear();
        yearSeg = `${String(startYear).slice(-2)}-${String(startYear + 1).slice(-2)}`;
      } else {
        yearSeg = String(new Date().getFullYear());
      }
    }
    return cfg.include_year ? `${cfg.prefix}-${yearSeg}-${seq}` : `${cfg.prefix}-${seq}`;
  };

  const save = async () => {
    setSaving(true);
    try {
      const payload = {};
      for (const [k, v] of Object.entries(series)) {
        payload[k] = {
          prefix: (v.prefix || "").toUpperCase(),
          next_number: Number(v.next_number || 1),
          pad: Number(v.pad || 4),
          include_year: Boolean(v.include_year),
          use_fy: Boolean(v.use_fy),
        };
      }
      const { data } = await api.put("/settings/document-series", payload);
      setSeries(data);
      toast.success("Document series settings saved");
    } catch (e) {
      toast.error(formatApiError(e?.response?.data?.detail) || e.message);
    } finally { setSaving(false); }
  };

  return (
    <div data-testid="settings-page">
      <PageHeader
        title="Settings"
        subtitle="Customize document numbering — prefix, starting number, padding and year suffix."
        actions={<button onClick={save} disabled={saving} className="btn-primary inline-flex items-center gap-1.5" data-testid="save-settings"><FloppyDisk size={14} weight="bold" /> {saving ? "Saving…" : "Save Changes"}</button>}
      />

      <div className="p-8 max-w-5xl space-y-6">
        <div className="card-base p-5">
          <div className="flex items-start gap-3 bg-[#FCEFD4] border border-[#E5C98A] rounded-md p-3 mb-5">
            <Warning size={18} weight="fill" className="text-[#8A5A19] mt-0.5" />
            <div className="text-xs text-[#6B4612] leading-relaxed">
              Changing the <span className="font-semibold">Next Number</span> resets the counter for that document type — the very next document you create will use this exact number. Use with care if you already have documents in the system.
            </div>
          </div>

          <div className="overflow-x-auto">
            <table className="table-base">
              <thead><tr>
                <th>Document Type</th>
                <th>Prefix</th>
                <th className="text-right">Next Number</th>
                <th className="text-right">Padding</th>
                <th className="text-center">Include Year</th>
                <th className="text-center">FY (Apr-Mar)</th>
                <th>Preview</th>
              </tr></thead>
              <tbody>
                {Object.entries(series).map(([dt, cfg]) => (
                  <tr key={dt} data-testid={`series-row-${dt}`}>
                    <td className="font-semibold">{cfg.label}</td>
                    <td>
                      <input className="field font-mono uppercase" style={{ width: 110 }} maxLength={8} value={cfg.prefix} onChange={e => update(dt, { prefix: e.target.value.toUpperCase() })} data-testid={`series-prefix-${dt}`} />
                    </td>
                    <td className="text-right">
                      <input type="number" min="1" className="field text-right tabular" style={{ width: 110 }} value={cfg.next_number} onChange={e => update(dt, { next_number: e.target.value })} data-testid={`series-next-${dt}`} />
                    </td>
                    <td className="text-right">
                      <select className="field text-right" style={{ width: 70 }} value={cfg.pad} onChange={e => update(dt, { pad: Number(e.target.value) })}>
                        {[2,3,4,5,6,7,8].map(n => <option key={n} value={n}>{n}</option>)}
                      </select>
                    </td>
                    <td className="text-center">
                      <input type="checkbox" checked={!!cfg.include_year} onChange={e => update(dt, { include_year: e.target.checked })} data-testid={`series-year-${dt}`} />
                    </td>
                    <td className="text-center">
                      <input type="checkbox" checked={!!cfg.use_fy} onChange={e => update(dt, { use_fy: e.target.checked })} data-testid={`series-fy-${dt}`} />
                    </td>
                    <td className="font-mono text-xs text-brand font-semibold">{preview(cfg)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>

        {/* FY Counter Override (per-FY editor) */}
        {fyList.length > 0 && (
          <div className="card-base p-5" data-testid="fy-counters-card">
            <div className="flex items-center justify-between mb-4">
              <div>
                <div className="flex items-center gap-2 font-display font-semibold">
                  <CalendarDots size={18} weight="duotone" /> Financial Year Counters
                </div>
                <div className="text-xs text-ink-muted mt-0.5">India FY runs <span className="font-semibold">1 Apr – 31 Mar</span>. Counters auto-rollover on 1 Apr. Pick a previous FY below to inspect or fix its sequence.</div>
              </div>
              <div>
                <label className="label-overline mb-1 block">Financial Year</label>
                <select className="field" value={selectedFy} onChange={e => setSelectedFy(e.target.value)} data-testid="fy-select">
                  {fyList.map(f => <option key={f} value={f}>{f}{f === currentFy ? "  (current)" : ""}</option>)}
                </select>
              </div>
            </div>

            <div className="overflow-x-auto">
              <table className="table-base">
                <thead><tr>
                  <th>Document Type</th>
                  <th>Counter Key</th>
                  <th className="text-right">Last Used Seq</th>
                  <th className="text-right">Next Number</th>
                  <th>Preview (next)</th>
                  <th className="text-right">Action</th>
                </tr></thead>
                <tbody>
                  {counters.length === 0 && <tr><td colSpan={6} className="text-center py-6 text-ink-muted">No counters yet for {selectedFy}.</td></tr>}
                  {counters.map(c => (
                    <tr key={c.key} data-testid={`counter-row-${c.doc_type}`}>
                      <td className="font-semibold">{c.label}</td>
                      <td className="font-mono text-xs text-ink-muted">{c.key}</td>
                      <td className="text-right tabular">{c.seq}</td>
                      <td className="text-right">
                        <input type="number" min="1" className="field text-right tabular" style={{ width: 110 }} defaultValue={c.next_number} onChange={e => {
                          const v = e.target.value;
                          setCounters(rows => rows.map(r => r.key === c.key ? { ...r, _newNext: v } : r));
                        }} data-testid={`counter-next-${c.doc_type}`} />
                      </td>
                      <td className="font-mono text-xs text-brand font-semibold">{c.preview}</td>
                      <td className="text-right">
                        <button
                          onClick={async () => {
                            setSavingCounter(c.key);
                            try {
                              await api.patch("/settings/counters", { key: c.key, next_number: Number(c._newNext ?? c.next_number) });
                              toast.success(`${c.label} next number set`);
                              const r = await api.get("/settings/counters", { params: { fy: selectedFy } });
                              setCounters(r.data.rows);
                            } catch (err) {
                              toast.error(formatApiError(err?.response?.data?.detail) || err.message);
                            } finally { setSavingCounter(null); }
                          }}
                          disabled={savingCounter === c.key}
                          className="btn-secondary inline-flex items-center gap-1 text-xs"
                          data-testid={`counter-save-${c.doc_type}`}
                        >
                          <ArrowsClockwise size={12} weight="bold" /> Set
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        )}
      </div>

      <FieldStyle />
    </div>
  );
}
